import React, { useState, useEffect } from 'react';
import Navbar from '../Navbar/Navbar';
import TopBar from '../Navbar/Mainbar.jsx';
import { getBackendUrl } from '../../../api';

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // holiday object or null
  const [formDate, setFormDate] = useState('');
  const [formReason, setFormReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Calendar view
  const [calMonth, setCalMonth] = useState(new Date());

  const backendUrl = getBackendUrl();

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${backendUrl}/api/holidays`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setHolidays(data.holidays || data || []);
      } else {
        setError('Failed to load holidays');
      }
    } catch (e) {
      setError('Network error loading holidays');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHolidays(); }, []);

  const openAdd = () => {
    setEditing(null);
    setFormDate('');
    setFormReason('');
    setError('');
    setShowForm(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setFormDate(h.date.slice(0, 10));
    setFormReason(h.reason || '');
    setError('');
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const handleSave = async () => {
    if (!formDate) { setError('Please select a date'); return; }
    if (!formReason.trim()) { setError('Please enter a reason / holiday name'); return; }
    setSaving(true);
    setError('');
    try {
      const method = editing ? 'PUT' : 'POST';
      const body = editing
        ? { id: editing.id, date: formDate, reason: formReason.trim() }
        : { date: formDate, reason: formReason.trim() };
      const res = await fetch(`${backendUrl}/api/holidays`, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        setSuccess(editing ? 'Holiday updated!' : 'Holiday added!');
        closeForm();
        fetchHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.error || 'Failed to save holiday');
      }
    } catch (e) {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (h) => {
    if (!window.confirm(`Delete "${h.reason || h.date}"?`)) return;
    try {
      const res = await fetch(`${backendUrl}/api/holidays`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: h.id, date: h.date }),
      });
      if (res.ok) {
        setSuccess('Holiday deleted');
        fetchHolidays();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError('Failed to delete');
      }
    } catch { setError('Network error'); }
  };

  // ── Mini calendar ─────────────────────────────────────────────────────────
  const getIsoLocal = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const holidayDates = new Set(holidays.map(h => h.date.slice(0, 10)));
  const yr = calMonth.getFullYear(), mo = calMonth.getMonth();
  const firstDay = new Date(yr, mo, 1).getDay();
  const dim = new Date(yr, mo + 1, 0).getDate();
  const todayIso = getIsoLocal(new Date());
  const MN = ['January','February','March','April','May','June',
               'July','August','September','October','November','December'];
  const calCells = [...Array(firstDay).fill(null), ...Array.from({length:dim},(_,i)=>i+1)];

  // Upcoming holidays sorted
  const upcoming = [...holidays]
    .filter(h => h.date.slice(0, 10) >= todayIso)
    .sort((a, b) => a.date.localeCompare(b.date));
  const past = [...holidays]
    .filter(h => h.date.slice(0, 10) < todayIso)
    .sort((a, b) => b.date.localeCompare(a.date));

  const fmtDate = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };
  const dayName = (iso) => {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'long' });
  };

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f9fafb' }}>
      <Navbar currentPage="Holidays" />
      <div className='Admin-holidays-container' style={{ flex: 1, display: 'flex', flexDirection: 'column', marginLeft: '125px' }}>
        <TopBar />
        <div style={{ padding: '32px', maxWidth: '1100px', margin: '0 auto', width: '100%' }}>

          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 700, color: '#111827' }}>
                🗓️ Government Holidays
              </h1>
              <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '14px' }}>
                Manage official holidays — these dates are automatically disabled in leave date pickers
              </p>
            </div>
            <button onClick={openAdd} style={{
              background: '#2563eb', color: '#fff', border: 'none', borderRadius: '8px',
              padding: '10px 20px', fontSize: '14px', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              + Add Holiday
            </button>
          </div>

          {/* Success / Error banners */}
          {success && (
            <div style={{ background: '#dcfce7', border: '1px solid #16a34a', borderRadius: '8px',
              padding: '12px 16px', marginBottom: '16px', color: '#15803d', fontSize: '14px' }}>
              ✅ {success}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '24px', alignItems: 'start' }}>

            {/* Left: holiday list */}
            <div>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>Loading holidays…</div>
              ) : holidays.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '48px', background: '#fff',
                  borderRadius: '12px', border: '1px solid #e5e7eb', color: '#6b7280' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>📅</div>
                  <p style={{ margin: 0, fontWeight: 500 }}>No holidays added yet</p>
                  <p style={{ margin: '4px 0 0', fontSize: '13px' }}>Click "+ Add Holiday" to get started</p>
                </div>
              ) : (
                <>
                  {/* Upcoming */}
                  {upcoming.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600,
                        color: '#374151', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Upcoming ({upcoming.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {upcoming.map(h => (
                          <HolidayCard key={h.id || h.date} h={h} fmtDate={fmtDate} dayName={dayName}
                            onEdit={openEdit} onDelete={handleDelete} upcoming />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Past */}
                  {past.length > 0 && (
                    <div>
                      <h3 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600,
                        color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Past ({past.length})
                      </h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {past.map(h => (
                          <HolidayCard key={h.id || h.date} h={h} fmtDate={fmtDate} dayName={dayName}
                            onEdit={openEdit} onDelete={handleDelete} upcoming={false} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Right: mini calendar */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e5e7eb', padding: '20px', position: 'sticky', top: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <button onClick={() => setCalMonth(new Date(yr, mo - 1, 1))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280' }}>‹</button>
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#111827' }}>{MN[mo]} {yr}</span>
                <button onClick={() => setCalMonth(new Date(yr, mo + 1, 1))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: '#6b7280' }}>›</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px', marginBottom: '6px' }}>
                {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                  <div key={d} style={{ textAlign: 'center', fontSize: '11px', fontWeight: 600, padding: '4px 0',
                    color: d === 'Su' || d === 'Sa' ? '#ef4444' : '#9ca3af' }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' }}>
                {calCells.map((d, i) => {
                  if (!d) return <div key={i} />;
                  const obj = new Date(yr, mo, d);
                  //const iso = obj.toISOString().slice(0, 10);
                  const iso = getIsoLocal(obj);
                  const isHol = holidayDates.has(iso);
                  const isWE = obj.getDay() === 0 || obj.getDay() === 6;
                  const isToday = iso === todayIso;
                  return (
                    <div key={i} title={isHol ? holidays.find(h=>h.date.slice(0,10)===iso)?.reason : ''}
                      style={{ padding: '5px 0', borderRadius: '4px', textAlign: 'center', fontSize: '12px',
                        background: isHol ? '#fef2f2' : isToday ? '#eff6ff' : 'transparent',
                        color: isWE || isHol ? '#ef4444' : '#111827',
                        fontWeight: isHol || isToday ? 700 : 400,
                        border: isHol ? '1px solid #fca5a5' : isToday ? '1px solid #93c5fd' : 'none',
                        cursor: isHol ? 'pointer' : 'default' }}>
                      {d}
                    </div>
                    
                  );
                })}
              </div>
              <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280', display: 'flex', gap: '12px' }}>
                <span><span style={{ color: '#ef4444' }}>●</span> Holiday</span>
                <span><span style={{ color: '#ef4444' }}>●</span> Weekend</span>
              </div>
              <div style={{ marginTop: '12px', padding: '10px', background: '#f9fafb', borderRadius: '8px', fontSize: '12px', color: '#374151' }}>
                <strong>{holidayDates.size}</strong> holiday{holidayDates.size !== 1 ? 's' : ''} total
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '420px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 20px', fontSize: '18px', fontWeight: 700, color: '#111827' }}>
              {editing ? '✏️ Edit Holiday' : '➕ Add Holiday'}
            </h2>

            {error && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px',
                padding: '10px 14px', marginBottom: '16px', color: '#dc2626', fontSize: '13px' }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600,
                color: '#374151', marginBottom: '6px' }}>Date *</label>
              <input type="date" value={formDate} onChange={e => setFormDate(e.target.value)}
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600,
                color: '#374151', marginBottom: '6px' }}>Holiday Name / Reason *</label>
              <input type="text" value={formReason} onChange={e => setFormReason(e.target.value)}
                placeholder="e.g. Diwali, Republic Day, Christmas"
                style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db',
                  borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }} />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={closeForm}
                style={{ padding: '10px 20px', border: '1px solid #d1d5db', borderRadius: '6px',
                  background: '#fff', color: '#374151', fontSize: '14px', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '10px 20px', border: 'none', borderRadius: '6px',
                  background: saving ? '#93c5fd' : '#2563eb', color: '#fff',
                  fontSize: '14px', fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? 'Saving…' : (editing ? 'Update' : 'Add Holiday')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const HolidayCard = ({ h, fmtDate, dayName, onEdit, onDelete, upcoming }) => (
  <div style={{
    background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb',
    padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    opacity: upcoming ? 1 : 0.6, transition: 'box-shadow 0.15s',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
      <div style={{
        width: '48px', height: '48px', borderRadius: '10px', flexShrink: 0,
        background: upcoming ? '#fef2f2' : '#f3f4f6',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
      }}>
        <span style={{ fontSize: '16px', fontWeight: 700, color: upcoming ? '#ef4444' : '#9ca3af', lineHeight: 1 }}>
          {new Date(h.date.slice(0,10) + 'T00:00:00').getDate()}
        </span>
        <span style={{ fontSize: '10px', fontWeight: 600, color: upcoming ? '#ef4444' : '#9ca3af', lineHeight: 1 }}>
          {new Date(h.date.slice(0,10) + 'T00:00:00').toLocaleDateString('en-GB', {month:'short'})}
        </span>
      </div>
      <div>
        <p style={{ margin: 0, fontWeight: 600, fontSize: '14px', color: '#111827' }}>{h.reason || '—'}</p>
        <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>
          {dayName(h.date.slice(0,10))} · {fmtDate(h.date.slice(0,10))}
        </p>
      </div>
    </div>
    <div style={{ display: 'flex', gap: '8px' }}>
      <button onClick={() => onEdit(h)}
        style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px',
          background: '#fff', color: '#374151', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
        Edit
      </button>
      <button onClick={() => onDelete(h)}
        style={{ padding: '6px 12px', border: '1px solid #fca5a5', borderRadius: '6px',
          background: '#fff', color: '#ef4444', fontSize: '12px', cursor: 'pointer', fontWeight: 500 }}>
        Delete
      </button>
    </div>
  </div>
);

export default Holidays;
